// Tunable constants for the pose-annotation app. Centralized so future
// adjustments (threshold tweaks, palette swaps) are a one-file change.
// Spec reference: spec.md §10.

import type { KeypointName } from '../types';

export const CONFIDENCE_THRESHOLD = 0.99;

export const COLORS = {
  qualified: '#22c55e',
  unqualified: '#ef4444',
  selected: '#f59e0b',
  pickedTick: '#9ca3af', // neutral gray — untouched (picked-but-unannotated) frame tick
  unsavedTick: '#f97316', // orange — touched-but-unsaved frame tick (legend + bar share this one color)
  auditedTick: '#22c55e', // green — saved frame tick
  // Skeleton lines are colored by body side (spec table), not by confidence.
  edgeLeft: '#00A9E0', // cyan — left-side body line
  edgeRight: '#E69F00', // orange — right-side body line
  edgeCross: '#9ca3af', // neutral gray — cross-body edge (left_shoulder–right_shoulder)
  activeViewBorder: '#3b82f6',
  epipolarCurve: '#ff5000', // orange, matches PoseStudio
} as const;

export const JOINT_COLORS = {
  shoulder: '#2F80ED',
  // hip: '#e69f00',
  pelvis: '#F2C94C',
  elbow: '#9B51E0',
  // wrist: '#f0e442',
} as const;

export const KEYPOINT_COLORS: Record<KeypointName, string> = {
  left_shoulder: JOINT_COLORS.shoulder,
  right_shoulder: JOINT_COLORS.shoulder,
  // left_hip: JOINT_COLORS.hip,
  // right_hip: JOINT_COLORS.hip,
  left_elbow: JOINT_COLORS.elbow,
  right_elbow: JOINT_COLORS.elbow,
  // left_wrist: JOINT_COLORS.wrist,
  // right_wrist: JOINT_COLORS.wrist,
  pelvis: JOINT_COLORS.pelvis,
};

// Marker shape per joint family (spec table): shoulder=circle, elbow=square,
// pelvis=star. Used by the canvas overlay and the panel/legend icons.
export type KeypointShape = 'circle' | 'square' | 'star';

export const JOINT_SHAPES = {
  shoulder: 'circle',
  pelvis: 'star',
  elbow: 'square',
} as const satisfies Record<string, KeypointShape>;

export const KEYPOINT_SHAPES: Record<KeypointName, KeypointShape> = {
  left_shoulder: JOINT_SHAPES.shoulder,
  right_shoulder: JOINT_SHAPES.shoulder,
  left_elbow: JOINT_SHAPES.elbow,
  right_elbow: JOINT_SHAPES.elbow,
  pelvis: JOINT_SHAPES.pelvis,
};

export const FRAME_STEP_LARGE = 30;

// Radii are expressed in image-pixel space (overlay SVG uses an image-sized viewBox).
export const DOT_RADIUS_PX = 4.5;
export const SELECTED_DOT_RADIUS_PX = 7;
export const QUALIFIED_DOT_STROKE_PX = 1;
export const UNQUALIFIED_DOT_STROKE_PX = 2;

export const LOCKED_DOT_ALPHA = 0.5;
